import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { promptsAPI } from '../services/api';

const PromptEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [form, setForm] = useState({
    name: '',
    description: '',
    prompt_template: '',
    variables: '', // texto livre (JSON opcional)
    category: '',
    tone: '',
    length: '',
    language: 'pt-BR',
    is_active: 1,
  });

  const [meta, setMeta] = useState({ usage_count: 0, created_at: null, updated_at: null });

  const loadPrompt = useCallback(async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await promptsAPI.get(id);
      const p = res.data?.data || res.data || {};
      setForm({
        name: p.name || '',
        description: p.description || '',
        prompt_template: p.prompt_template || '',
        variables: typeof p.variables === 'string' ? p.variables : (p.variables ? JSON.stringify(p.variables, null, 2) : ''),
        category: p.category || '',
        tone: p.tone || '',
        length: p.length || '',
        language: p.language || 'pt-BR',
        is_active: p.is_active ? 1 : 0,
      });
      setMeta({
        usage_count: p.usage_count ?? 0,
        created_at: p.created_at || null,
        updated_at: p.updated_at || null,
      });
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Erro ao carregar prompt');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadPrompt(); }, [loadPrompt]);

  const setField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = { ...form };
      // "variables" vai como string; backend pode armazenar como TEXT. Se quiser enviar JSON, tente parse seguro.
      if (form.variables) {
        try {
          const parsed = JSON.parse(form.variables);
          payload.variables = parsed; // envia como objeto; backend pode serializar
        } catch (_) {
          // Mantém como texto se não for JSON válido
        }
      }
      payload.is_active = form.is_active ? 1 : 0;

      const res = await promptsAPI.update(id, payload);
      if (res.status >= 200 && res.status < 300) {
        setSuccess('Prompt atualizado com sucesso!');
        await loadPrompt();
      } else {
        setError(res.data?.error || 'Falha ao salvar');
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="prompt-edit-page">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2>✏️ Editar Prompt</h2>
        <div>
          <button className="page-btn" onClick={() => navigate('/prompts')} style={{ marginRight: '0.5rem' }}>Voltar</button>
          <button className="page-btn" onClick={save} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>

      {loading && <div className="loading" style={{ marginTop: '1rem' }}>Carregando...</div>}
      {error && <div className="error" style={{ color: '#dc3545', marginTop: '1rem' }}>{error}</div>}
      {success && <div className="success" style={{ color: '#28a745', marginTop: '1rem' }}>{success}</div>}

      {!loading && (
        <div className="card" style={{ marginTop: '1rem', padding: '1rem' }}>
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label>Nome</label>
              <input type="text" value={form.name} onChange={e => setField('name', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Categoria</label>
              <input type="text" value={form.category} onChange={e => setField('category', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Tom</label>
              <input type="text" value={form.tone} onChange={e => setField('tone', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Tamanho</label>
              <input type="text" value={form.length} onChange={e => setField('length', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Idioma</label>
              <input type="text" value={form.language} onChange={e => setField('language', e.target.value)} />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label>Ativo</label>
              <input type="checkbox" checked={!!form.is_active} onChange={e => setField('is_active', e.target.checked ? 1 : 0)} />
            </div>
          </div>

          <div className="form-group" style={{ marginTop: '12px' }}>
            <label>Descrição</label>
            <textarea value={form.description} onChange={e => setField('description', e.target.value)} rows={3} />
          </div>

          <div className="form-group system-instruction" style={{ marginTop: '12px' }}>
            <label>Template do Prompt</label>
            <textarea value={form.prompt_template} onChange={e => setField('prompt_template', e.target.value)} rows={10} />
          </div>

          <div className="form-group" style={{ marginTop: '12px' }}>
            <label>Variáveis (JSON opcional)</label>
            <textarea value={form.variables} onChange={e => setField('variables', e.target.value)} rows={6} placeholder='Ex.: {"topic": "..."}' />
            <small style={{ color: '#6c757d' }}>Se JSON válido, será enviado como objeto; caso contrário, como texto.</small>
          </div>

          <div className="meta" style={{ marginTop: '16px', color: '#666' }}>
            <div>Uso: <strong>{meta.usage_count}</strong></div>
            <div>Criado: {meta.created_at ? new Date(meta.created_at).toLocaleString('pt-BR') : '-'}</div>
            <div>Atualizado: {meta.updated_at ? new Date(meta.updated_at).toLocaleString('pt-BR') : '-'}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptEdit;